import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useNavigate } from "react-router-dom"

import { LoginPageFormData } from "./FormData.js"
import { authorize, generateRandomValue } from "../../AuthConfig.js"
import { ErrorField } from "../../components/Common/Form/Error/index.js"
import { InputBox } from "../../components/Common/Form/InputBox/InputBox.js"
import Loader from "../../components/Common/Loader/loader"
import ToastNotification, { notify } from "../../components/Toast/index.js"
import {getExternalUserDetails, getUserDetails} from "../../services/api.js"
import { ForgetPasswordRequest, GetAllCountryConfigList,UserLoginDetails, UserLoginVerification } from "../../services/mutations.js"
import { useGetUserDetails } from "../../services/queries.js"
import { isValidEmailRegx } from "../../utils/Constant.js"
import { customNotification } from "../../utils/utils.js"
import RequestNewPassword from "./RequestNewPassword.js"
import VerificationPage from "./Verification.js"

const REDIRECT_URL = "/dashboard"
const REQUIRED_VALUE = "PLEASE ENTER THE VALUE"

const Login = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { requestPasswordFormData,
        verificationFormData,
        logInFormData,
        requestPasswordValidationRules,
        defaultPages,
        verificationValidationRules
    } = LoginPageFormData()
    const [loader, setLoader] = useState(false)
    const [isResendCode, setIsResendCode] = useState(false)
    const [isPngUser, setIsPngUser] = useState(true)
    const [isNonPngUser, setIsNonPngUser] = useState(false)
    const [errors, setErrors] = useState({})
    const { mutateAsync: getUserLoginDetails } = UserLoginDetails()
    const { mutateAsync: forgetPasswordRequest } = ForgetPasswordRequest()
    const { mutateAsync: userLoginVerification } = UserLoginVerification()
    const [formData,setFormData]=useState("")
    const [pages,setPages]=useState(defaultPages)
    
    const setAllPagesFalse = () => {
        return Object.keys(defaultPages).reduce((acc, key) => {
            acc[key] = false
            return acc
        }, {})
    }

    const { mutateAsync: GetAllCountryConfig } = GetAllCountryConfigList()

    const setActivePage=(page)=>{
        const allPagesFalse = setAllPagesFalse()
        setPages({
            ...allPagesFalse,
            [page] : true
        })
    }

    const loginDetails = sessionStorage.getItem("userDetails")
    const loggedInUserDetails = loginDetails
        ? JSON.parse(loginDetails)?.user_details
        : {}
    let onKeyDownHandler
    const useKeyDown = (callback, keys) => {
        onKeyDownHandler = (event) => {
            const wasAnyKeyPressed = keys.some((key) => event.key === key);
            if (wasAnyKeyPressed) {
                callback();
            }
        };
    }


    useKeyDown(() => {
        if (pages?.logInPage) {
            verifyUser()
        }
        else {
            onSubmit()
        }
    }, ["Enter"]);

    useEffect(() => {
        document.addEventListener('keydown', onKeyDownHandler);
        return () => {
            document.removeEventListener('keydown', onKeyDownHandler);
        };
    }, [onKeyDownHandler]);


    const verifyUser = async (event) => {
        event?.preventDefault()
        sessionStorage.clear()
        localStorage.clear()
        if(sessionStorage.getItem("access_token"))
            sessionStorage.removeItem("access_token")

        if(sessionStorage.getItem("countryDetails")){
            sessionStorage.removeItem("countryDetails")
        }

        if(sessionStorage.getItem("dateFormatBasedOnCountry")){
            sessionStorage.removeItem("dateFormatBasedOnCountry")
        }

        if(isNonPngUser){
            const isValidation = validate()
            if (!isValidation) {
                return
            }
        }
        
        setLoader(true)
        let email = formData?.email?.split("@")[1]

        if(isPngUser || email === "pg.com"){
            let state = generateRandomValue()
            let nonce = generateRandomValue()
  
            sessionStorage.setItem("state", state)
            sessionStorage.setItem("nonce", nonce)
        
            if(!sessionStorage.getItem("access_token")){
                authorize(state, nonce)
                
            }else{
                setLoader(false)
                window.location.href = REDIRECT_URL
            }
        }else{
            try {
                const loginResponse = await userLoginVerification({
                    email: formData?.email,
                    password: formData?.password
                })
                if(loginResponse?.forgetPassword){
                    navigate("/setPassword", { state: { emailId: formData?.email } })
                }
                else{
                    if(!isResendCode){
                        setActivePage("verificationPage")
                    }
                }                
                setLoader(false)

            } catch (error) {
                setLoader(false)
                notify("ERROR", customNotification(error?.message, t))

            }
        }
    }

    const onInputChange = (e) => {
        e.preventDefault()
        const { name, value } = e.target
        if(pages?.requestPasswordPage){
            setFormData({ ...formData,[name]: value })
        }else if(pages?.verificationPage){
            setFormData({ ...formData,[name]: value })
        }else{
            setFormData({ ...formData, [name]: value })
        }
        setErrors({
            ...errors,
            [name]: ""
        })
    }

    const validate = () => {
        const errors = {}
        if (!formData?.email) {
            errors.email = t("errorMessage.email")
            setErrors({ ...errors }, errors)
        } else if (!isEmail(formData?.email)) {
            errors.email = t("errorMessage.emailIsvalid")
            setErrors({ ...errors }, errors)
        }
        if (!formData?.password) {
            errors.password = t("errorMessage.passwordIsRequired")
            setErrors({ ...errors }, errors)
        }
        return Object.keys(errors).length === 0
    }

    const validateRequestPasswordAndVerification=(formData)=>{
        let isValid = true
        let validateRules = pages?.requestPasswordPage ? requestPasswordValidationRules : verificationValidationRules
        Object.keys(formData).forEach((element) => {
            const formFieldName =
        typeof formData[element] === "string"
            ? formData[element].trim()
            : formData[element]
            if (!formFieldName && validateRules[element]?.required) {
                setErrors((prevError) => ({
                    ...prevError,
                    [element]: REQUIRED_VALUE
                }))
                isValid = false
            }
            if(element === "email"){
                if(!formData[element].match(isValidEmailRegx)){
                    setErrors((prevError) => ({
                        ...prevError,
                        [element]: t("errorMessage.emailIsvalid")
                    }))
                    isValid = false
                }
            }
        })
        return isValid
    }

    const isEmail = (email) =>
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)

    const emailRef = useRef(null)
    const passwordRef = useRef(null)
    useEffect(() => {
        // Set focus to the input element on component mount
        if (passwordRef.current) {
            passwordRef.current.focus()
        }
        const timer=setTimeout(()=>{
            if (emailRef.current) {
                emailRef.current.focus()
            }
        }, 200)
        return()=>clearTimeout(timer)
    }, [])

    useEffect(() => {
        localStorage.setItem("selectedLanguage","")
        if(loggedInUserDetails?.user_name !== undefined){
            sessionStorage.removeItem("loginDetails")
        }
    }, [])

    useEffect(()=>{
        if(pages?.requestPasswordPage){
            setFormData({ ...requestPasswordFormData })
        }else if(pages?.verificationPage){
            setFormData({ ...verificationFormData,email:formData?.email,password: formData?.password })
        }else{
            setFormData({ ...logInFormData })
        }
        setErrors({})
    },[pages])

    const onChangeHand = (e) =>{
        if(e?.target?.name === "PG User"){
            setIsPngUser(true)
            setIsNonPngUser(false)
        }else{
            setIsNonPngUser(true)
            setIsPngUser(false)
        }
    }

    const onClickForgotPassword=(e)=>{
        e.preventDefault() 
        setActivePage("requestPasswordPage")
        setErrors({})
    }

    const onSubmit=async()=>{
        let isValidation = validateRequestPasswordAndVerification(formData)
        if (!isValidation) {
            return
        }
        if (isValidation) {
            setLoader(true)
            try {
                const result = pages?.requestPasswordPage ? 
                    await forgetPasswordRequest(formData) : await getUserLoginDetails(formData)
                if (result) {
                    //setLoginResponse(result)

                    if(!pages?.requestPasswordPage){
                        sessionStorage.setItem("loginDetails", JSON.stringify(result))
                        //sessionStorage.setItem("loginDetails", JSON.stringify(result))
                        sessionStorage.setItem("id_token", result?.mfaTokenData?.access_token)
                        let userId = {
                            "userId": formData?.email
                        }
                        let response = await getExternalUserDetails({
                            headers: {
                                Authorization: "Bearer " + result?.access_token,
                                locale: "en-US", //navigator.language
                                tenantid: 1,
                                usercontext: JSON.stringify(userId)
                            }
                        } )
                        if(response) {
                            setLoader(false)

                            sessionStorage.setItem("loginDetails", JSON.stringify(result))
                            localStorage.setItem("loginSuccess", true)
                            localStorage.setItem("selectedLanguageId", response?.languages[0]?.id) // Set Default Language ID
                            sessionStorage.setItem("userDetails", JSON.stringify(response))

                            navigate("/dashboard")
                        } else {
                            setLoader(false)
                            notify("ERROR", t("common.userdetails"))
                        }
                    }
                    if(pages?.requestPasswordPage){
                        setActivePage("logInPage")
                        setLoader(false)
                    }
                    if(pages?.verificationPage){
                        if (!result.access_token) {
                            setLoader(false)
                            return
                        }
                        setLoader(false)
                        setIsResendCode(false)                        
                        localStorage.setItem("loginSuccess", true)                        
                        //navigate("/home")
                    }
                }
            }
            catch(error){
                if (error.message) {
                    notify("ERROR", customNotification(error?.message, t))
                    setLoader(false)
    
                }
            }
        }
    }

    return (
        <>
            <Loader loading={ loader } />
            <div className={ " h-[80vh] max-h-[70vh] mb-[1em] flex justify-center items-center " }>
                { pages?.verificationPage && 
                    <VerificationPage 
                        formData={ formData }
                        errors={ errors }
                        onInputChange={ onInputChange }
                        setActivePage={ setActivePage }
                        onSubmit={ onSubmit }
                        onClickResendCode={ verifyUser }
                    /> 
                }
                {
                    pages?.logInPage &&
                        <form
                            onSubmit={ verifyUser }
                            className="flex bg-white border-solid border rounded-lg border-gray py-6 px-4 gap-6 flex-col w-[456px]"
                        >
                            <div>
                                <h2 className="text-2xl font-bold text-primary-50">
                                    { t("login.welcome") }
                                </h2>
                                <p className="text-base text-secondary-50 pt-1">
                                    { t("login.welcomeMsgSubText") }
                                </p>
                            </div>
                            <div className="flex gap-[2rem]">
                                <div className="relative flex gap-1">
                                    <input
                                        label={ t("PG User") }
                                        id="pguser"
                                        data-test-id="pg user"
                                        aria-label = { "pg user" }
                                        type="radio"
                                        name="PG User"
                                        value={ isPngUser }       
                                        onChange={ (e) => onChangeHand(e) }
                                        checked = { isPngUser }
                                        className="w-[18px] h-[18px] border border-primary-50 focus:ring-primary-50 text-primary-50"
                                        
                                    />
                                    <label htmlFor={ "pguser" }                    
                                        className="text-sm text-secondary-50 font-normal"
                                    >
                                        { t("P&G User ")   }
                                    </label>
                                
                                </div>

                                <div className="relative flex gap-1">
                                    <input
                                        label={ t("Non PG User") }
                                        id="nonpguser"
                                        data-test-id="nonpg user"
                                        aria-label = { "nonpg user" }
                                        type="radio"
                                        name="NonPG User"
                                        value={  isNonPngUser }  
                                        checked = { isNonPngUser } 
                                        onChange={ (e) => onChangeHand(e) }
                                        className="w-[18px] h-[18px] border border-primary-50 focus:ring-primary-50 text-primary-50"
                                    />
                                    <label htmlFor={ "pguser" }                    
                                        className="text-sm text-secondary-50 font-normal"
                                    >
                                        { t("Non-P&G User") }
                                    </label>
                                </div>
                            </div>

                            { isNonPngUser &&
                                <div className="gap-4 flex flex-col">
                                    <div className="">
                                        <InputBox
                                            inputRef={ emailRef }
                                            id="email"
                                            type="text"
                                            name="email"
                                            label={ t("login.email") }
                                            placeholder=""
                                            onChangeHandler={ (e) => onInputChange(e) }
                                            value={ formData?.email || "" }
                                            defaultValue={ "" }
                                            ariaLabel = { "email" }

                                        />
                                        <ErrorField name={ errors?.email } />
                                        
                                    </div>
                                    <div className="">
                                        <InputBox
                                            inputRef={ passwordRef }
                                            id="password"
                                            type="password"
                                            name="password"
                                            ariaLabel = { "password" }
                                            label={ t("login.password") }
                                            placeholder=""
                                            value={ formData?.password || "" }
                                            textHideRequired={ true }
                                            onChangeHandler={ (e) => onInputChange(e) }
                                        />
                                        <ErrorField name={ errors?.password } />
                                    </div>
                                    { /* THIS IS TEMPORARY COMMENTED , ONCE FUNCTONALITY IMPLEMENTED THIS WILL BE ENABLED*/ }
                                    <NavLink to="" onClick={ (e)=>onClickForgotPassword(e) } className="text-base text-primary-100 underline">
                                        { t("login.forgotPassword") }
                                    </NavLink>
                                </div>
                            }
                            <button
                                type="submit"
                                id="loginSubmit"
                                className="text-white border border-solid hover:border-primary-150 
 bg-primary-100 hover:bg-primary-150 border-primary-50 hover:text-primary-50 
 font-bold text-base px-6 py-3 outline-none focus:outline-1 
 focus:outline-black focus:outline-offset-2 rounded-full 
 disabled:bg-primary-300 disabled:text-secondary-50 disabled:border-0"
                            >
                                { t("login.submit") }
                            </button>
                            <p className="text-base text-[#5D6065]">
                                { t("login.dontHaveAccount") }{ " " }
                                <NavLink to="/registration" className="text-[#003DA5] text-base underline">
                                    { t("login.regRquest")  }
                                </NavLink>
                                <span className="p-1">({ t ("login.nonPG") })</span>
                            
                            </p>
                        </form>
                }
                {
                    pages?.requestPasswordPage && 
                        <RequestNewPassword
                            formData={ formData }
                            errors={ errors }
                            onInputChange={ onInputChange }
                            setActivePage={ setActivePage }
                            onSubmit={ onSubmit }
                        />
                }
            </div>
            <div className="flex justify-center items-center sticky__footer sticky bottom-0 bg-white px-10 py-3
             border-lightGray pb-2 pt-3">
                <div className="text-base text-[#5D6065] text-secondary-50">
                    <span>{ t("login.reachOutDesc") }</span>
                    <span className={ "text-base text-[#5D6065] font-bolder text-blue p-2" }>{ "gas.service@pg.com" }</span>
                </div>

            </div> 
            <ToastNotification/>
        </>
    )
}

export default Login
